<script setup lang="ts">
import type { FlightEvent } from "@ca/db/schema";
import type { FlightFatigueTrigger } from "@ca/db/schema/flight-events";

import {
	computed, ref, watch,
} from "vue";

import { useMouse } from "@vueuse/core";

import {
	FATIGUE_FLIGHT_EVENT_TYPE, SINK_RATE_FLIGHT_EVENT_TYPE,
} from "@ca/db/schema/flight-events";
import { formatTimeToSecond } from "@ca/formatters/dates";
import { formatDistance } from "@ca/formatters/distance";
import { riskLevelToColor } from "@ca/risk/flight";

import {
	mostSevereSinkRateRiskLevel, sinkRateBandToRiskLevel,
} from "@/utils/sink_rate";


const props = defineProps<{
	events: FlightEvent[];
}>();

const { sourceType } = useMouse();
const wasEverTouch = ref(false);

watch(sourceType, (val) => {
	if (val === "touch") {
		wasEverTouch.value = true;
	}
});

const tipMode = computed(() => {
	if (wasEverTouch.value) {
		return "click";
	}
	return "hover";
});
  
const labelMap: Record<string, string> = {
	[FATIGUE_FLIGHT_EVENT_TYPE]: "Pilot Fatigue",
	[SINK_RATE_FLIGHT_EVENT_TYPE]: "Sink Rate Exceedance",
};

const defaultIcon = {
	class: "text-red-500 size-5",
	name: "ic-round-warning",
};

const iconMap: Record<string, { class: string; name: string }> = {
	[FATIGUE_FLIGHT_EVENT_TYPE]: {
		class: "text-blue-900 size-5",
		name: "ic-round-snooze",
	},
	[SINK_RATE_FLIGHT_EVENT_TYPE]: {
		class: "text-red-500 size-5",
		name: "ic-round-warning",
	},
};

const fatigueTriggerLabelMap: Record<FlightFatigueTrigger["type"], string> = {
	duty_day_duration: "Duty day duration",
	flight_time: "Flight time",
	legs: "Leg count",
};

const formatFatigueTrigger = (trigger: FlightFatigueTrigger) => {
	const label = fatigueTriggerLabelMap[trigger.type];
	const unit = trigger.unit === "hours" ? "h" : "";
	return `${label} exceeded (${trigger.actual}${unit} > ${trigger.limit}${unit})`;
};

const getFatigueTriggers = (events: FlightEvent[]): FlightFatigueTrigger[] => {
	const triggersByType = new Map<FlightFatigueTrigger["type"], FlightFatigueTrigger>();
	for (const event of events) {
		if (event.type !== FATIGUE_FLIGHT_EVENT_TYPE) {
			continue;
		}
		if (!event.data || event.data.type !== FATIGUE_FLIGHT_EVENT_TYPE) {
			continue;
		}
		for (const trigger of event.data.triggers) {
			const existing = triggersByType.get(trigger.type);
			if (!existing || trigger.actual > existing.actual) {
				triggersByType.set(trigger.type, trigger);
			}
		}
	}
	return Array.from(triggersByType.values());
};

const getSinkRateEvents = (events: FlightEvent[]): { color: string; id: string; text: string }[] => {
	const sinkRateEvents = events
		.filter((event) => event.type === SINK_RATE_FLIGHT_EVENT_TYPE)
		.toSorted((left, right) => new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime());
	return sinkRateEvents
		.slice(0, 5)
		.map((event) => {
			const data = event.data?.type === SINK_RATE_FLIGHT_EVENT_TYPE ? event.data : null;
			const aglFt = data?.aglFt ?? null;
			const sinkRate = formatDistance({
				distance: event.value,
				unit: "foot-per-minute",
			});
			const agl = aglFt === null ? "" : ` at ${formatDistance({
				distance: aglFt,
				unit: "foot",
			})} AGL`;
			return {
				color: riskLevelToColor(sinkRateBandToRiskLevel(data?.band ?? "b")).hex,
				id: `${event.id}-${event.timestamp}`,
				text: `${sinkRate}${agl} at ${formatTimeToSecond(event.timestamp)}`,
			};
		});
};

const eventTypes = computed(() => {
	const eventsByType = new Map<string, FlightEvent[]>();
	for (const event of props.events) {
		const existing = eventsByType.get(event.type) ?? [];
		existing.push(event);
		eventsByType.set(event.type, existing);
	}
	return Array.from(eventsByType.entries()).map(([type, events]) => {
		const count = events.length;
		const label = labelMap[type] ?? type;
		const countText = count > 1 ? ` (${count})` : "";
		const fatigueTriggers = type === FATIGUE_FLIGHT_EVENT_TYPE ? getFatigueTriggers(events) : [];
		const sinkRateEvents = type === SINK_RATE_FLIGHT_EVENT_TYPE ? getSinkRateEvents(events) : [];
		const summaryText = `${label}${countText}`;
		const icon = iconMap[type] ?? defaultIcon;
		const bandColor = type === SINK_RATE_FLIGHT_EVENT_TYPE
			? riskLevelToColor(mostSevereSinkRateRiskLevel(events)).hex
			: null;
		return {
			count,
			fatigueTriggers,
			icon: {
				class: bandColor === null ? icon.class : "size-5",
				name: icon.name,
				style: bandColor === null ? undefined : { color: bandColor },
			},
			label,
			summaryText,
			sinkRateEvents,
			type,
		};
	});
});
  
</script>
    
<template>
  <div
    v-if="eventTypes.length > 0"
    class="flex items-center gap-2"
  >
    <UPopover
      v-for="eventType in eventTypes"
      :key="eventType.type"
      :open-delay="0"
      :close-delay="50"
      :mode="tipMode"
      arrow
    >
      <UIcon
        :class="eventType.icon.class"
        :style="eventType.icon.style"
        :name="eventType.icon.name"
      />
      <template #content>
        <div class="text-sm w-72 p-2">
          <h4 class="text-gray-500 text-sm font-cta mb-1">
            {{ eventType.summaryText }}
          </h4>
          <template v-if="eventType.type === FATIGUE_FLIGHT_EVENT_TYPE && eventType.fatigueTriggers.length > 0">
            <p class="mb-1">
              Flagged because one or more fatigue limits were exceeded:
            </p>
            <ul class="list-disc pl-4 space-y-1">
              <li
                v-for="trigger in eventType.fatigueTriggers"
                :key="trigger.type"
              >
                {{ formatFatigueTrigger(trigger) }}
              </li>
            </ul>
          </template>
          <template v-else-if="eventType.type === SINK_RATE_FLIGHT_EVENT_TYPE && eventType.sinkRateEvents.length > 0">
            <p class="mb-1">
              Exceeded sink-rate values:
            </p>
            <ul class="space-y-1">
              <li
                v-for="item in eventType.sinkRateEvents"
                :key="item.id"
                class="flex items-center gap-2"
              >
                <span
                  class="size-2 shrink-0 rounded-full"
                  :style="{ backgroundColor: item.color }"
                />
                {{ item.text }}
              </li>
            </ul>
          </template>
          <template v-else>
            {{ eventType.summaryText }}
          </template>
        </div>
      </template>
    </UPopover>
  </div>
</template>
    
